/**
 * Utils for handling parcel status operations
 * 
 * Now gets status colors directly from backend API responses
 * instead of hardcoded frontend mapping
 */

// Get color for a status - uses status_color from API response when available
export const getStatusColor = (status, item = null) => {
  // If we have an item object with status_color from API, use that
  if (item && item.status_color) {
    return item.status_color
  }
  
  // If status is an object with color property (from API response), use that
  if (typeof status === 'object' && status.color) {
    return status.color
  }
  
  // Fallback: basic color mapping for backward compatibility
  // This will rarely be used now that we get colors from backend
  if (!status) return 'secondary'
  
  const statusKey = typeof status === 'string' 
    ? status.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '').trim()
    : status
  
  // Basic fallback mapping — same colour assignments as backend
  // ShipmentStatus::getStatusColor. Legacy keys collapse to their merge target.
  const fallbackColors = {
    pending: 'warning',
    received: 'info',
    packaged: 'primary',
    shipped: 'secondary',
    arrived: 'info',
    counter_verified: 'success',
    ready_for_pickup: 'success',
    delivered: 'success',
    completed: 'success',
    empty: 'light',
    loaded: 'primary',
    // Legacy:
    pending_verification: 'warning',
    in_transit: 'secondary',
    port_arrived: 'secondary',
    customs_clearance: 'info',
    prepared_for_shipping: 'secondary',
  }
  
  return fallbackColors[statusKey] || 'secondary'
}

// Tabler icon per canonical status. Kept in sync with the dashboard
// summary tiles in getStatusSummaryItems().
const STATUS_ICONS = {
  pending: 'tabler-clock',
  received: 'tabler-inbox',
  packaged: 'tabler-box',
  shipped: 'tabler-ship',
  arrived: 'tabler-building-warehouse',
  counter_verified: 'tabler-circle-check',
  ready_for_pickup: 'tabler-bell',
  delivered: 'tabler-truck-delivery',
  claimed: 'tabler-message-circle',
}

// Get icon for a status - uses status_icon from API response when available,
// then falls back to the canonical Tabler icon map.
export const getStatusIcon = (status, item = null) => {
  if (item && item.status_icon) return item.status_icon
  if (typeof status === 'object' && status?.icon) return status.icon

  const statusKey = typeof status === 'string'
    ? status.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '').trim()
    : status

  return STATUS_ICONS[statusKey] || 'tabler-circle'
}

// Extract status name from parcel object
export const extractStatusName = parcel => {
  if (!parcel) return ''
  
  // Try different paths in the object to find status
  if (parcel.status_key) return parcel.status_key
  if (parcel.status?.name) return parcel.status.name
  
  return ''
}

// Canonical filter dropdown — same 7+1 list as the Incoming Parcels page.
export const getStatusOptions = t => [
  { title: t('shipment.all'), value: '' },
  { title: t('shipment.statuses.pending'), value: 'pending' },
  { title: t('shipment.statuses.received'), value: 'received' },
  { title: t('shipment.statuses.packaged'), value: 'packaged' },
  { title: t('shipment.statuses.shipped'), value: 'shipped' },
  { title: t('shipment.statuses.arrived'), value: 'arrived' },
  { title: t('shipment.statuses.counter_verified'), value: 'counter_verified' },
  { title: t('shipment.statuses.ready_for_pickup'), value: 'ready_for_pickup' },
  { title: t('shipment.statuses.delivered'), value: 'delivered' },
]

// Canonical state machine — counter_verified is a side-track from `arrived`,
// not part of the linear progression.
export const getStatusTransitions = () => {
  return {
    pending: [
      { status: 'received', label: 'verify_parcel' },
    ],
    received: [
      { status: 'packaged', label: 'package_parcel' },
    ],
    packaged: [
      { status: 'shipped', label: 'mark_as_shipped' },
    ],
    shipped: [
      { status: 'arrived', label: 'mark_as_warehouse_arrived' },
    ],
    arrived: [
      { status: 'ready_for_pickup', label: 'mark_as_ready_for_pickup' },
      { status: 'counter_verified', label: 'counter_verify' },
    ],
    counter_verified: [
      { status: 'ready_for_pickup', label: 'mark_as_ready_for_pickup' },
    ],
    ready_for_pickup: [
      { status: 'delivered', label: 'mark_as_delivered' },
    ],
  }
}

// Get available actions for a status (used in parcel details)
export const getAvailableActions = status => {
  const transitions = getStatusTransitions()
  
  return transitions[status] || []
}

// Dashboard summary cards — canonical statuses only. counter_verified is
// internal-only so it's not surfaced as a dashboard tile.
export const getStatusSummaryItems = t => computed(() => [
  { status: 'pending', label: t('shipment.statuses.pending'), color: 'warning', icon: 'tabler-clock', count: 0, displayStatus: 'pending' },
  { status: 'received', label: t('shipment.statuses.received'), color: 'info', icon: 'tabler-inbox', count: 0, displayStatus: 'received' },
  { status: 'packaged', label: t('shipment.statuses.packaged'), color: 'primary', icon: 'tabler-box', count: 0, displayStatus: 'packaged' },
  { status: 'shipped', label: t('shipment.statuses.shipped'), color: 'secondary', icon: 'tabler-ship', count: 0, displayStatus: 'shipped' },
  { status: 'arrived', label: t('shipment.statuses.arrived'), color: 'info', icon: 'tabler-building-warehouse', count: 0, displayStatus: 'arrived' },
  { status: 'ready_for_pickup', label: t('shipment.statuses.ready_for_pickup'), color: 'success', icon: 'tabler-bell', count: 0, displayStatus: 'ready_for_pickup' },
  { status: 'delivered', label: t('shipment.statuses.delivered'), color: 'success', icon: 'tabler-truck-delivery', count: 0, displayStatus: 'delivered' },
])
